import { useCallback, useEffect, useReducer } from 'react';

export const reducer = (state: any, { type, payload }: { type: string; payload: any }) => {
  switch (type) {
    case 'UPDATE_INITIAL_STATE':
      return {
        ...state,
        ...payload,
        hasChanged: false
      };
    case 'UPDATE_VALUE':
      return {
        ...state,
        hasChanged: true,
        [payload.key]: payload.value
      };

    case 'UPDATE_ERROR':
      return {
        ...state,
        isSubmitted: false,
        hasError: true,
        error: { ...state.error, ...payload.error }
      };

    case 'UPDATE_ERROR_MESSAGES':
      return {
        ...state,
        isSubmitted: false,
        hasError: true,
        errorMessages: payload.errorMessages
      };

    case 'RESET_ERRORS':
      return {
        ...state,
        hasError: false,
        error: payload.error,
        errorMessages: undefined
      };

    case 'RESET_VALUES':
      return {
        ...state,
        ...payload,
        isSubmitted: false,
        hasChanged: false
      };

    case 'RESET_FORM':
      const initialError: { [key: string]: boolean } = {};
      Object.keys(payload).forEach(key => {
        initialError[key] = false;
      });

      return {
        ...state,
        hasChanged: false,
        hasError: true,
        isSubmitted: false,
        errorMessages: undefined,
        error: initialError
      };

    default:
      return state;
  }
};

type FormSubmitProps = { state: any; resetForm: () => void };
interface FormStateProps {
  initialState?: {
    [key: string]: any;
  };
  validate?: (state: any) => boolean;
  onChange?: (state: any) => void;
  onSubmit: (props: FormSubmitProps) => void;
}

const useFormState = ({ initialState = {}, validate = () => true, onSubmit, onChange }: FormStateProps) => {
  const initialError: { [key: string]: boolean } = {};
  Object.keys(initialState).forEach(key => {
    initialError[key] = false;
  });

  const [state, dispatch] = useReducer(reducer, {
    hasError: true,
    hasChanged: false,
    isSubmitted: false,
    errorMessages: undefined,
    error: initialError,
    ...initialState
  });

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();

    dispatch({
      type: 'UPDATE_VALUE',
      payload: { key: 'isSubmitted', value: true }
    });

    const valid = validate(state);

    if (valid) {
      await onSubmit({ state, resetForm });
    }

    dispatch({
      type: 'UPDATE_VALUE',
      payload: { key: 'isSubmitted', value: false }
    });
  };

  const updateValue = (key: keyof typeof initialState, value: string) => {
    dispatch({ type: 'UPDATE_VALUE', payload: { key, value } });
  };

  const resetError = () => {
    if (!state.hasError) return;
    dispatch({ type: 'RESET_ERRORS', payload: { error: initialError } });
  };

  const setErrorMessages = (messages: string[]) => {
    dispatch({
      type: 'UPDATE_ERROR_MESSAGES',
      payload: {
        errorMessages: messages
      }
    });
  };

  useEffect(() => {
    if (onChange) onChange(state)
  }, [state])

  const updateInitialState = useCallback((state: any) => {
    dispatch({ type: 'RESET_VALUES', payload: state });
  }, []);

  const resetValues = () => {
    dispatch({ type: 'RESET_VALUES', payload: initialState });
  };

  const resetForm = () => {
    dispatch({ type: 'RESET_FORM', payload: initialState });
  };

  const setError = (error: { [key: string]: boolean }) => {
    dispatch({ type: 'UPDATE_ERROR', payload: { error } });
  };

  return {
    ...state,
    handleSubmit,
    updateValue,
    resetError,
    resetValues,
    updateInitialState,
    resetForm,
    setError,
    setErrorMessages
  };
};

export default useFormState;
